
import { useState } from "react";
import {
  Table,
  TableBody,
} from "@/components/ui/table";
import { StrategyProvider, ProviderWithUser, UserInfo } from "@/types/strategy-types";
import { ProviderFormDialog } from "./ProviderFormDialog";
import { UserProviderDialog } from "./UserProviderDialog";
import ProviderTableHeader from "./provider-table/ProviderTableHeader";
import ProviderTableRow from "./provider-table/ProviderTableRow";
import ProviderTableFilters from "./provider-table/ProviderTableFilters";
import DeleteProviderDialog from "./provider-table/DeleteProviderDialog";
import RemoveUserDialog from "./provider-table/RemoveUserDialog";
import EmptyProviderTable from "./provider-table/EmptyProviderTable";

interface ProviderTableProps {
  providers: ProviderWithUser[];
  users: UserInfo[];
  onCreateProvider: (provider: Omit<StrategyProvider, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  onUpdateProvider: (id: string, provider: Partial<StrategyProvider>) => Promise<void>;
  onDeleteProvider: (id: string) => Promise<void>;
  onToggleActive: (id: string, isActive: boolean) => Promise<void>;
  onAssignUser: (providerId: string, userId: string) => Promise<void>;
  onRemoveUser: (providerId: string, userId: string) => Promise<void>;
}

const ProviderTable = ({
  providers,
  users,
  onCreateProvider,
  onUpdateProvider,
  onDeleteProvider,
  onToggleActive,
  onAssignUser,
  onRemoveUser
}: ProviderTableProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<ProviderWithUser | null>(null);
  const [deletingProviderId, setDeletingProviderId] = useState<string | null>(null);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [currentProviderId, setCurrentProviderId] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  
  const filteredProviders = providers.filter((provider) =>
    provider.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditClick = (provider: ProviderWithUser) => {
    setEditingProvider(provider);
  };

  const handleDeleteClick = (providerId: string) => {
    setDeletingProviderId(providerId);
  };

  const handleConfirmDelete = async () => {
    if (deletingProviderId) {
      await onDeleteProvider(deletingProviderId);
      setDeletingProviderId(null);
    }
  };

  const handleToggleActive = async (providerId: string, isActive: boolean) => {
    await onToggleActive(providerId, isActive);
  };

  const handleAssignUserClick = (providerId: string) => {
    setCurrentProviderId(providerId);
    setIsUserDialogOpen(true);
  };

  const handleAssignUser = async (userId: string) => {
    if (currentProviderId) {
      await onAssignUser(currentProviderId, userId);
      setIsUserDialogOpen(false);
      setCurrentProviderId(null);
    }
  };

  const handleRemoveUserClick = (providerId: string, userId: string) => {
    setCurrentProviderId(providerId);
    setRemovingUserId(userId);
  };

  const handleConfirmRemoveUser = async () => {
    if (currentProviderId && removingUserId) {
      await onRemoveUser(currentProviderId, removingUserId);
      setRemovingUserId(null);
      setCurrentProviderId(null);
    }
  };

  return (
    <div className="space-y-4">
      <ProviderTableFilters 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddNew={() => setIsCreateDialogOpen(true)}
      />

      <div className="rounded-md border">
        <Table>
          <ProviderTableHeader />
          <TableBody>
            {filteredProviders.length === 0 ? (
              <EmptyProviderTable />
            ) : (
              filteredProviders.map((provider) => (
                <ProviderTableRow
                  key={provider.id}
                  provider={provider}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteClick}
                  onToggleActive={handleToggleActive}
                  onAssignUser={handleAssignUserClick}
                  onRemoveUser={handleRemoveUserClick}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ProviderFormDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={onCreateProvider}
      />

      {editingProvider && (
        <ProviderFormDialog
          isOpen={true}
          onClose={() => setEditingProvider(null)}
          onSubmit={(data) => onUpdateProvider(editingProvider.id, data)}
          initialData={editingProvider}
        />
      )}

      <UserProviderDialog
        isOpen={isUserDialogOpen}
        onClose={() => setIsUserDialogOpen(false)}
        onAssign={handleAssignUser}
        users={users.filter(user => user.role === 'strategy_provider' && !providers.some(p => p.user?.id === user.id))}
      />

      <DeleteProviderDialog 
        isOpen={!!deletingProviderId}
        onClose={() => setDeletingProviderId(null)}
        onConfirm={handleConfirmDelete}
      />

      <RemoveUserDialog 
        isOpen={!!removingUserId}
        onClose={() => setRemovingUserId(null)}
        onConfirm={handleConfirmRemoveUser}
      />
    </div>
  );
};

export default ProviderTable;
